import { ItemStatusBadge } from '@/components/badges/ItemStatusBadge'
import { OnlineStatusDot } from '@/components/ItemOnlineStatus'
import { SectionCard } from '@/components/SectionCard'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ItemStatus, itemStatusConfig } from '@/constants/item'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useGetProductItems } from '@/features/equipment/hooks/useProduct'
import { useGetQuotations } from '@/features/quotations/hooks/useQuotation'
import { useConfirm } from '@/hooks/useConfirm'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useDebounce } from '@/hooks/useDebounce'
import { useUI } from '@/hooks/useUI'
import { formatBuildingFloorLocation } from '@/utils/location'
import { getMutationOptions } from '@/utils/mutation'
import {
  ArrowLeft,
  DollarSign,
  Edit2,
  Eye,
  FileText,
  Hash,
  MapPin,
  Package,
  Plus,
  QrCode,
  Search,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { QRCodeManager } from '../../qrcode/components/QRCodeManager'
import { QuotationList } from '../../quotations/components/QuotationList'
import { RequestQuotationDialog } from '../../quotations/components/RequestQuotationDialog'
import { useDeleteItem } from '../hooks/useItem'
import { ItemDetailDialog } from './ItemDetailDialog'
import { ItemDialog } from './ItemDialog'

type ItemsViewProps = {
  equipmentId: string
}

export function ItemsView({ equipmentId }: ItemsViewProps) {
  const { t } = useTranslation()
  const { formatDateTime } = useDateFormatter()
  const { itemStatuses, language, getEnumLabel } = useUI()
  const { isAdmin } = useAuth()
  const confirm = useConfirm()
  const deleteItem = useDeleteItem()

  const [activeTab, setActiveTab] = useState<'items' | 'quotations' | 'qrcode'>('items')
  const [itemStatusFilter, setItemStatusFilter] = useState<ItemStatus | 'all'>('all')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)

  const { data: productItems, isFetching: isFetchingItems } = useGetProductItems(equipmentId, {
    status: itemStatusFilter === 'all' ? undefined : itemStatusFilter,
    search: debouncedSearch || undefined,
  })

  const { data: quotations = [], isLoading: isLoadingQuote } = useGetQuotations(
    { productId: equipmentId },
    !!equipmentId,
  )

  const navigate = useNavigate()

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [itemOpen, setItemOpen] = useState(false)
  const [itemDetailOpen, setItemDetailOpen] = useState(false)

  const [requestQuoteOpen, setRequestQuoteOpen] = useState(false)

  const activeCount = quotations.filter(
    q => q.status === 'Submitted' && q.validUntil && new Date(q.validUntil) >= new Date(),
  ).length
  const pendingCount = quotations.filter(q => q.status === 'Pending').length
  const quotationCount = activeCount + pendingCount

  const canManage = isAdmin

  const product = productItems?.product
  const items = productItems?.items ?? []

  const handleDeleteItem = async (itemId: string) => {
    const name = items.find(i => i.id === itemId)?.serialNumber
    const confirmed = await confirm({
      variant: 'destructive',
      title: t('common.deleteConfirmTitle'),
      description: t('equipment.item.confirmDeleteDescription', { name }),
    })

    if (confirmed) {
      setSelectedItemId(null)

      const options = getMutationOptions({
        successMessage: t('equipment.item.toast.deleteSuccess'),
      })
      deleteItem.mutate(itemId, options)
    }
  }

  return (
    <div data-testid="equipment-items-page" className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            data-testid="equipment-items-back"
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground"
            onClick={() => {
              setItemStatusFilter('all')
              setActiveTab('items')
              navigate(`/equipment`)
            }}
          >
            <ArrowLeft className="mr-1 size-3" /> {t('common.back')}
          </Button>
          <div className="flex flex-col">
            <h2 data-testid="equipment-items-title" className="text-sm font-semibold text-foreground">
              {product?.name}
            </h2>
            <span data-testid="equipment-items-subtitle" className="text-xs text-muted-foreground">
              {product?.brand} {product?.model} - {product?.categoryName}
            </span>
          </div>
        </div>
        {canManage && (
          <Button
            data-testid="equipment-items-request-quotation"
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => setRequestQuoteOpen(true)}
          >
            <FileText className="mr-1 size-3" />
            {t('equipment.item.requestQuotation')}
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as 'items' | 'quotations' | 'qrcode')}>
        <SectionCard
          isLoading={isFetchingItems}
          header={
            <div className="flex justify-center sm:justify-start">
              <TabsList className="flex flex-col w-full h-full items-start sm:flex-row">
                <TabsTrigger
                  data-testid="equipment-items-tab-items"
                  value="items"
                  className="text-xs w-full justify-center"
                >
                  <Package className="mr-1 size-3" />
                  {t('equipment.item.title')} ({items.length})
                </TabsTrigger>
                <TabsTrigger
                  data-testid="equipment-items-tab-quotations"
                  value="quotations"
                  className="text-xs w-full justify-center"
                >
                  <DollarSign className="mr-1 size-3" />
                  {t('equipment.item.quotation')} ({quotationCount})
                </TabsTrigger>
                <TabsTrigger
                  data-testid="equipment-items-tab-qrcode"
                  value="qrcode"
                  className="text-xs w-full justify-center"
                >
                  <QrCode className="mr-1 size-3" />
                  {t('common.qrcode')}
                </TabsTrigger>
              </TabsList>
            </div>
          }
          right={
            <>
              {activeTab === 'items' && (
                <>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      data-testid="equipment-items-search-input"
                      placeholder={t('common.placeholder.searchItem')}
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="h-full w-full sm:w-[200px] bg-secondary/50 pl-8 text-xs"
                    />
                  </div>
                  <Select value={itemStatusFilter} onValueChange={v => setItemStatusFilter(v as ItemStatus | 'all')}>
                    <SelectTrigger
                      data-testid="equipment-items-status-filter"
                      className="h-8 w-[140px] bg-secondary/50 text-xs"
                    >
                      <SelectValue placeholder="สถานะ" />
                    </SelectTrigger>
                    <SelectContent className="bg-card text-foreground">
                      <SelectItem value="all">{t('common.allStatuses')}</SelectItem>
                      {itemStatuses?.map(role => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {canManage && (
                    <Button
                      data-testid="equipment-items-create-button"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => {
                        setSelectedItemId(null)
                        setItemOpen(true)
                      }}
                    >
                      <Plus className="mr-1 size-3" />
                      {t('equipment.item.add')}
                    </Button>
                  )}
                </>
              )}
            </>
          }
        >
          <TabsContent value="items" className="mt-0">
            <div className="overflow-x-auto rounded-lg border border-border/30">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/30 hover:bg-transparent">
                    <TableHead></TableHead>
                    <TableHead className="text-xs text-muted-foreground">{t('equipment.item.serialNumber')}</TableHead>
                    <TableHead className="text-xs text-muted-foreground">{t('equipment.item.status')}</TableHead>
                    <TableHead className="text-xs text-muted-foreground">{t('equipment.item.department')}</TableHead>
                    <TableHead className="text-xs text-muted-foreground">{t('equipment.item.location')}</TableHead>
                    <TableHead className="text-xs text-muted-foreground">
                      {t('equipment.item.warrantyExpire')}
                    </TableHead>
                    <TableHead className="text-xs text-muted-foreground">{t('equipment.item.nextMa')}</TableHead>
                    <TableHead className="sr-only text-xs">{t('common.view')}</TableHead>
                    <TableHead className="sr-only text-xs">{t('common.edit')}</TableHead>
                    <TableHead className="sr-only text-xs">{t('common.delete')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="equipment-items-table">
                  {items?.map(item => {
                    return (
                      <TableRow
                        key={item.id}
                        data-testid={`equipment-item-row-${item.id}`}
                        className="border-border/20 hover:bg-secondary/40"
                        onClick={() => {}}
                      >
                        <TableCell>
                          <div className="flex justify-center">
                            <OnlineStatusDot
                              supportsOnlineStatus={item.supportsOnlineStatus}
                              onlineStatus={item.onlineStatus}
                            />
                          </div>
                        </TableCell>
                        <TableCell
                          data-testid={`equipment-item-serial-${item.id}`}
                          className="text-xs font-mono text-foreground"
                        >
                          {item.serialNumber}
                        </TableCell>
                        <TableCell>
                          <ItemStatusBadge status={item.status} />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{item?.departmentName || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="flex max-w-[180px] md:max-w-[300px] items-center gap-1 overflow-hidden">
                                  <MapPin className="size-3 shrink-0" />

                                  <span className="truncate">
                                    {[formatBuildingFloorLocation(t, item?.buildingName, item?.floorName)]
                                      .filter(Boolean)
                                      .join(' / ')}
                                  </span>
                                </span>
                              </TooltipTrigger>

                              <TooltipContent className="flex md:hidden">
                                {[formatBuildingFloorLocation(t, item?.buildingName, item?.floorName)]
                                  .filter(Boolean)
                                  .join(' / ')}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDateTime(item.warrantyExpiry, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }) || '-'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDateTime(item.nextMaintenance, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }) || '-'}
                        </TableCell>
                        <TableCell>
                          <Button
                            data-testid={`equipment-item-view-${item.id}`}
                            variant="ghost"
                            size="sm"
                            className="h-6 text-[10px] text-primary hover:text-primary"
                            onClick={e => {
                              e.stopPropagation()
                              setSelectedItemId(item.id)
                              setItemDetailOpen(true)
                            }}
                          >
                            <Eye className="mr-1 size-3" />
                            {t('common.view')}
                          </Button>
                          {canManage && (
                            <>
                              <Button
                                data-testid={`equipment-item-edit-${item.id}`}
                                variant="ghost"
                                size="sm"
                                className="h-6 text-[10px] text-muted-foreground"
                                onClick={e => {
                                  e.stopPropagation()
                                  setSelectedItemId(item.id)
                                  setItemOpen(true)
                                }}
                              >
                                <Edit2 className="mr-1 size-3" />
                                {t('common.edit')}
                              </Button>
                              <Button
                                data-testid={`equipment-item-delete-${item.id}`}
                                variant="ghost"
                                size="sm"
                                className="h-6 text-[10px] text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => {
                                  handleDeleteItem(item.id)
                                }}
                              >
                                <Trash2 className="mr-1 size-3" />
                                {t('common.delete')}
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                  {items?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                        {t('equipment.item.noEquipment')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          <TabsContent value="quotations" className="mt-0">
            <QuotationList quotations={quotations} />
          </TabsContent>
          <TabsContent value="qrcode" className="mt-0">
            <QRCodeManager
              data={items}
              type="equipment"
              getId={i => i.id}
              renderInfo={item => {
                const config = itemStatusConfig[item.status]

                return (
                  <>
                    <span className="text-xs font-semibold">{product?.name}</span>

                    <span className="flex items-center gap-1 font-mono text-[11px] text-primary">
                      <Hash className="size-2.5" />
                      {item.serialNumber}
                    </span>

                    <span className={`text-[10px] ${config.color}`}>{getEnumLabel(itemStatuses, item.status)}</span>
                  </>
                )
              }}
              getPrintLabel={item => ({
                title: product?.categoryName ?? '',
                subtitle: item.serialNumber,
              })}
            />
          </TabsContent>
        </SectionCard>
      </Tabs>

      {product && (
        <ItemDialog
          open={itemOpen}
          onOpenChange={setItemOpen}
          product={productItems?.product}
          itemId={selectedItemId}
        />
      )}
      <ItemDetailDialog open={itemDetailOpen} onOpenChange={setItemDetailOpen} itemId={selectedItemId} />
      <RequestQuotationDialog open={requestQuoteOpen} onOpenChange={setRequestQuoteOpen} productId={equipmentId} />
    </div>
  )
}
